import React, { useState, useEffect, useCallback } from 'react';
import { costApi, projectApi, wbsApi, taxApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, Lightning, Receipt, ArrowsClockwise } from '@phosphor-icons/react';

import { fmtCurrency } from '../utils/currencyFormat';

export default function CostsPage() {
  const [costs, setCosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterType, setFilterType] = useState('');
  const [deriving, setDeriving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    project_id: '', activity_id: '', description: '', amount: '', expense_type: 'other', expense_date: '',
    input_tax_amount: '0', tax_rate_id: ''
  });
  const [taxRates, setTaxRates] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities(), taxApi.listRates(true)])
      .then(([p, a, t]) => { setProjects(p.data); setActivities(a.data); setTaxRates(t.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadCosts = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterType) params.cost_type = filterType;
    const res = await costApi.list(params);
    setCosts(res.data);
  }, [filterProject, filterType]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  const fmt = (n) => fmtCurrency(n);

  const handleDerive = async () => {
    if (!filterProject) { alert('Select a project first'); return; }
    setDeriving(true);
    try {
      const res = await costApi.derive(filterProject);
      alert(`Derived ${res.data.derived_count} new cost entries with journal entries`);
      loadCosts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to derive'); }
    finally { setDeriving(false); }
  };

  const openCreate = () => {
    setForm({
      project_id: filterProject || '', activity_id: '', description: '',
      amount: '', expense_type: 'other', expense_date: new Date().toISOString().split('T')[0],
      input_tax_amount: '0', tax_rate_id: ''
    });
    setError(''); setShowModal(true);
  };

  const handleExpTaxRateChange = (rateId) => {
    const rate = taxRates.find(r => r.id === rateId);
    const amt = parseFloat(form.amount) || 0;
    const taxAmt = rate ? Math.round(amt * rate.percentage / 100 * 100) / 100 : 0;
    setForm({ ...form, tax_rate_id: rateId, input_tax_amount: String(taxAmt) });
  };

  const handleExpAmountChange = (val) => {
    const amt = parseFloat(val) || 0;
    const rate = taxRates.find(r => r.id === form.tax_rate_id);
    const taxAmt = rate ? Math.round(amt * rate.percentage / 100 * 100) / 100 : parseFloat(form.input_tax_amount) || 0;
    setForm({ ...form, amount: val, input_tax_amount: String(taxAmt) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const rate = taxRates.find(r => r.id === form.tax_rate_id);
      await costApi.createExpense({
        project_id: form.project_id,
        activity_id: form.activity_id,
        description: form.description,
        amount: parseFloat(form.amount) || 0,
        expense_type: form.expense_type,
        expense_date: form.expense_date || null,
        input_tax_amount: parseFloat(form.input_tax_amount) || 0,
        tax_rate_id: form.tax_rate_id || null,
        tax_rate_percentage: rate ? rate.percentage : null,
        tax_rate_name: rate ? rate.tax_name : null,
      });
      setShowModal(false); loadCosts();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await costApi.delete(id); loadCosts(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const totalCost = costs.reduce((s, c) => s + (c.amount || 0), 0);
  const byType = {};
  costs.forEach(c => { byType[c.cost_type] = (byType[c.cost_type] || 0) + (c.amount || 0); });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="costs-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Cost Tracking</h1>
          <p className="text-sm text-[#666]">Auto-derived from execution + manual expenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleDerive} disabled={deriving || !filterProject} data-testid="derive-costs-btn">
            <ArrowsClockwise className={`w-4 h-4 mr-2 ${deriving ? 'animate-spin' : ''}`} /> {deriving ? 'Deriving...' : 'Derive Costs'}
          </Button>
          <Button onClick={openCreate} data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-2" /> Add Expense</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type">
          <option value="">All Types</option>
          <option value="labor">Labor</option>
          <option value="material">Material</option>
          <option value="other">Other</option>
        </Select>
        <div className="bg-[#002FA7]/10 text-[#002FA7] px-3 py-2 rounded-lg text-sm font-bold flex items-center justify-center" data-testid="total-cost">
          Total: {fmt(totalCost)}
        </div>
      </div>

      {/* Type breakdown */}
      {Object.keys(byType).length > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(byType).map(([t, v]) => (
            <div key={t} className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-1.5 text-xs">
              <span className="text-[#666] capitalize">{t}: </span>
              <span className="font-bold text-[#0A0A0A]">{fmt(v)}</span>
            </div>
          ))}
        </div>
      )}

      {costs.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No costs tracked yet. Select a project and click "Derive Costs" to auto-capture from execution data.</Card>
      ) : (
        <div className="space-y-2">
          {costs.map(c => (
            <Card key={c.id} className="p-3" data-testid={`cost-row-${c.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.source_type === 'manual' ? <Receipt className="w-4 h-4 text-[#FFAB00]" /> : <Lightning className="w-4 h-4 text-[#002FA7]" />}
                    <span className="text-sm font-medium text-[#0A0A0A]">{c.description}</span>
                    <Badge variant={c.cost_type === 'labor' ? 'primary' : c.cost_type === 'material' ? 'success' : 'warning'}>{c.cost_type}</Badge>
                    {c.source_type !== 'manual' && <span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded">auto</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#666]">
                    <span>{c.activity_name}</span>
                    <span>{c.project_name}</span>
                    {c.log_date && <span>{c.log_date}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-right">
                    <span className="font-bold text-sm text-[#0A0A0A]" data-testid={`cost-amount-${c.id}`}>{fmt(c.amount)}</span>
                    {c.input_tax_amount > 0 && (
                      <p className="text-[10px] text-[#00C853]">+Tax: {fmt(c.input_tax_amount)}</p>
                    )}
                  </div>
                  {c.source_type === 'manual' && (
                    <button onClick={() => handleDelete(c.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-cost-${c.id}`}><Trash className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Manual Expense">
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="expense-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required data-testid="expense-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required data-testid="expense-activity">
            <option value="">-- Select --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="expense-desc" />
          <Input label="Amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => handleExpAmountChange(e.target.value)} required data-testid="expense-amount" />
          <Select label="Input Tax Rate" value={form.tax_rate_id} onChange={(e) => handleExpTaxRateChange(e.target.value)} data-testid="expense-tax-rate">
            <option value="">No Tax</option>
            {taxRates.map(r => <option key={r.id} value={r.id}>{r.tax_name} ({r.percentage}%)</option>)}
          </Select>
          <Input label="Input Tax Amount" type="number" min="0" step="0.01" value={form.input_tax_amount} onChange={(e) => setForm({ ...form, input_tax_amount: e.target.value })} data-testid="expense-tax-amount" />
          <Select label="Type" value={form.expense_type} onChange={(e) => setForm({ ...form, expense_type: e.target.value })} data-testid="expense-type">
            <option value="other">Other</option>
            <option value="equipment">Equipment</option>
            <option value="transport">Transport</option>
            <option value="subcontractor">Subcontractor</option>
          </Select>
          <Input label="Date" type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date" />
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-expense-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
